import '@shared/style/root.scss'
import Vue from 'vue'
import App from '@/App.vue'
// import DirectLinkApp from '@/DirectLinkApp.vue'
import VueRouter from 'vue-router'
import router from '@/assets/js/router'
import VueBar from 'vuebar'
import VueFilter from 'vue-filter'
import directives from '@/assets/js/directives.js'
import filters from '@/assets/js/filters.js'
import VueDynamic from 'vue-dynamic'
import MultiLanguage from '@/assets/js/vue-multilanguage-edited.js'
import Languages from '@/assets/translations/main.js'
import Notify from 'vue-notification'
import * as Sentry from '@sentry/browser'
import * as Integrations from '@sentry/integrations'
import * as amplitude from '@amplitude/analytics-browser'
import { plugin as amplitudeEngagementPlugin } from '@amplitude/engagement-browser'
amplitude.add(amplitudeEngagementPlugin())
import VueResource from 'vue-resource'
import { EventBus, signOut, Users, getEnvironment } from '@/assets/js/helpers.js'
import { store } from '@/assets/js/store'
import { throttle } from 'lodash'
import VueKeepAliveDev from 'vue-keep-alive-dev'
import PortalVue from 'portal-vue'
import tooltip from '@/directives/tooltip.ts'
import '@lottiefiles/dotlottie-wc'

Vue.config.productionTip = false

Vue.use(VueBar)
Vue.use(VueFilter)
Vue.use(VueDynamic, { name: 'dynamic' })
Vue.use(MultiLanguage, Languages)
Vue.use(Notify)
Vue.use(VueKeepAliveDev)
Vue.use(PortalVue)
Vue.use(VueResource)

// list of deprecated apps, the key is the deprecated app name, the value is the new app name.
const deprecatedApps = {
  'lassousersettings': 'lassosettings'
}

// if we're developing on the direct link version of the portal, set below to true:
let isDirectLink = false

// if we're developing on the product version of the portal, set below to true:
// let isProductAccess = true

const environment = getEnvironment()

if (environment !== 'Development') {
  // sentry
  Sentry.init({
    dsn: 'https://e681300aa62545ba85a4809710d8ca7c@sentry.io/28414',
    ignoreErrors: [
      'Non-Error promise rejection captured'
    ],
    integrations: [
      new Integrations.Vue({
        Vue,
        attachProps: true,
      }),
    ],
    environment: getEnvironment()
  })
}

let amplitudeApiKey

switch (getEnvironment()) {
  case 'Production':
    amplitudeApiKey = '47d310bda836420af3367e181c9320de'
    break
  case 'Development':
  case 'Staging':
  case 'Dev1':
  case 'Dev2':
  case 'Dev3':
    amplitudeApiKey = '469194e3161124bdb0228c3e226a56ff'
    break
  }

// #region Amplitude Analytics
let amplitudePromise = null

const ensureAmplitudeInitialized = async () => {
  if (amplitudePromise) return amplitudePromise
  
  amplitudePromise = new Promise(async (resolve, reject) => {
    await store.dispatch('initialize')
    if (store.state.impersonating) {
      // do not track anything when we're impersonating.
      resolve(false)
    } else {
      try {
        // first initialize Amplitude
        amplitude.init(amplitudeApiKey, {
          fetchRemoteConfig: false,
          serverZone: 'EU',
          autocapture: {
            attribution: false,
            pageViews: false,
            sessions: true,
            formInteractions: false,
            fileDownloads: false,
            elementInteractions: false,
          }
        })

        resolve(true)
      } catch (error) {
        Sentry.captureException(new Error(`Could not initialize Amplitude: ${error.message}`))
        resolve(false)
      }
    }
  })
  
  return amplitudePromise
}

EventBus.$on('amplitude:trackEvent', async ({ event, traits = {} }) => {
  if(!(await ensureAmplitudeInitialized())) return
  setTimeout(() => {
    traits.userBrowserId = store.state.userBrowserId
    amplitude.track(event, traits)
  }, 10)
})

EventBus.$on('amplitude:identifyUser', async ({ id, traits = {} }) => {
  if(!(await ensureAmplitudeInitialized())) return
  let identify = new amplitude.Identify()
  Object.keys(traits).forEach(k => {
    identify.set(k, traits[k])
  })
  amplitude.identify(identify)
})

EventBus.$on('amplitude:trackPage', async () => {
  // if(!(await ensureAmplitudeInitialized())) return
  // setTimeout(() => {
  //   amplitude.track('Page Viewed', {
  //     Url: window.location.href,
  //     Path: window.location.pathname,
  //     Search: window.location.search,
  //     Title: document.title,
  //     Referrer: document.referrer
  //   })
  // }, 10)
})
//#endregion

// setup directives
Object.keys(directives).forEach(function (key) {
  Vue.directive(key, directives[key])
})
Vue.directive('tooltip', tooltip)

/* setup filters */
Object.keys(filters).forEach(function (key) {
  Vue.filter(key, filters[key])
})

store.dispatch('initialize').then(() => {
  // initialize Lasso product SDK
  if (isDirectLink || store.state.direct) window.lasso.initialize({ product: 'lasso-direct', version: '1.0.0' })
  // else if (isProductAccess || window.isProductAccess) window.lasso.initialize({ product: 'lasso-product', version: '1.0.0' })
  else window.lasso.initialize({ product: 'lasso', version: '1.0.0' })
  
  router.beforeEach(async (to, from, next) => {
    // if we're in "direct link" mode, then we have to make sure to persist the "direct" query param, so that we can refresh the page and still stay in direct mode.
    if (store.state.direct) {
      // do not go to route if direct link and not entity, app or home view
      if (to.name !== 'entity' && to.name !== 'app' && to.name !== 'search') next({ name: 'search', replace: true })
  
      // do not allow going to lassosettings in direct link mode
      if (to.params.appName == 'lassosettings') next({ name: 'search', replace: true })

      // make sure we can't go to the home screen without having the search bar visible.
      if (to.name === 'search' && to.query.search === undefined) {
        router.replace({ name: 'search', params: {}, query: { ...from.query, ...to.query, search: null } })
        return
      }
  
      // keep any query parameters across routes
      if (from.name && to.path !== from.path && JSON.stringify(from.query) !== JSON.stringify(to.query)) {
        let newTo = { name: to.name, params: to.params, query: JSON.parse(JSON.stringify(from.query)) }
        next(newTo)
        return
      }
    }
  
    if (store.state.lockedByModal) next(false)
    else {
      switch (to.name) {
        case 'list':
          await store.dispatch('getTags')
          let list = store.getters.tag(to.params.listId)
          if (list)  next()
          else next({ name: 'search', replace: true })
          return
        case 'app':
          if (to.params.appName in deprecatedApps) {
            next(`/app/${deprecatedApps[to.params.appName]}/${to.params.viewName || ''}`)
            return
          }
          var app = store.getters.app(to.params.appName)
          if (app || store.getters.appFromSolution(to.params.appName)) next()
          else next({ name: 'search', replace: true, query: to.query })
          return
        default: next()
      }
    }
  })

  // Initializes the router with the current location, including query parameters.
  // This is required because of the way the router is used now
  router.replace({
    path: location.pathname,
    hash: location.hash,
    query: Object.fromEntries(new URLSearchParams(location.search).entries()),
  }).catch(reason => {
    // Hiding router error. Reference: https://stackoverflow.com/a/65326844/6020382
    if (import.meta.env.DEV) console.debug(reason)
  })
})

let doThrottledActivityBroadcast = throttle(() => {
  store.dispatch('broadcastActivity')
}, 10000, { leading: true })

router.afterEach((to, from) => {
  if (to.name === 'entity') {
    store.dispatch('getEntity', { lassoId: to.params.lassoId }).then(entity => {
      // document.title = entity.name
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    }, () => {})
    if (to.params.appName) setTimeout(() => EventBus.$emit('amplitude:trackPage'), 1000)
  } else {
    setTimeout(() => EventBus.$emit('amplitude:trackPage'), 1000)
  }


  // broadcast activity to push server in order to prevent multiple sessions for the same user. Only do this if not read-only (e.g. direct link shared by many users) or trial.
  if (!store.getters.allowMultipleSessions) doThrottledActivityBroadcast()
})

Vue.use(VueRouter)

Vue.config.ignoredElements = [
  'dotlottie-wc',
];

/* eslint-disable no-new */
new Vue({
  el: '#app',
  router,
  store,
  template: '<FullApp/>',
  data: {
    loadingMessage: null
  },
  components: {
    FullApp: App,
    // DirectLinkApp,
    VueBar
  },
  beforeMount () {
    // hack to add the translate function to vuex.
    this.$store.translate = this.translate
  },
  mounted () {
    // we need to set inital tab document title
    EventBus.$on('token-changed', tokens => {
      if (tokens.newToken) { // still logged in, but token has changed.
        if (store.state.direct) return // TODO: We should probably also log the user out here, but the below currently fails as we have not loaded a user.
        Users.getCurrent().then(user => {
          if (store.state.user.id !== user.id) { // The user has also changed
            EventBus.$emit('modal', {
              modalType: 'confirm',
              confirmTitle: this.translate('sign-in-other-user'),
              confirmDescription: this.translate('sign-in-other-user-description'),
              confirmButtonText: this.translate('continue'),
              hideCancelButton: true,
              onConfirm: () => window.location.replace('/'),
              requireAction: true
            })
          }
        })
      } else if (!tokens.newToken) {
        EventBus.$emit('modal', {
          modalType: 'confirm',
          confirmTitle: this.translate('signed-out'),
          confirmDescription: this.translate('signed-out-description'),
          confirmButtonText: this.translate('log-in-again'),
          hideCancelButton: true,
          onConfirm: () => signOut(),
          requireAction: true
        })
      }
    })
    window.addEventListener('blur', event => EventBus.$emit('app-click', event))
  }
})
